import { useEffect, useRef, type ReactNode } from 'react'

import { App2CasesProvider } from 'src/components/App2Cases/App2CasesContext'
import { App2CaseNavigation } from 'src/components/App2CaseNavigation/App2CaseNavigation'
import {
  App2FrameTintProvider,
  useApp2FrameTint,
} from 'src/components/App2Shell/app2FrameTint'
import { UiProvider } from 'src/design-system/ui'
import { MainScrollProvider } from 'src/layouts/_shared/MainScrollContext'
import { useParams } from 'src/platform/router'

import { ProfessionalDocumentCanvas } from '../_shared/ProfessionalDocumentCanvas'
import { ProfessionalTopBar } from '../_shared/ProfessionalTopBar'
import { useProfessionalMode } from '../_shared/useProfessionalMode'
import '../_shared/professional-shell.css'
import './app2.css'

const App2Frame = ({
  children,
  applicationId,
  ...appearance
}: {
  children: ReactNode
  applicationId: number | null
} & ReturnType<typeof useProfessionalMode>) => {
  const mainRef = useRef<HTMLElement>(null)
  const { background } = useApp2FrameTint()
  const content = (leadingContent?: ReactNode) => (
    <div className="app2-layout-column">
      <ProfessionalTopBar leadingContent={leadingContent} {...appearance} />
      <main ref={mainRef} className="app2-layout-main">
        <MainScrollProvider value={mainRef}>{children}</MainScrollProvider>
      </main>
    </div>
  )
  return (
    <div
      className="app2-layout"
      style={background ? { backgroundColor: background } : undefined}
    >
      <ProfessionalDocumentCanvas />
      {applicationId === null ? (
        content()
      ) : (
        <App2CaseNavigation>{content}</App2CaseNavigation>
      )}
    </div>
  )
}

/** Professional owns the document and mode; legacy leaves own neither. */
const App2DashboardLayout = ({ children }: { children: ReactNode }) => {
  const appearance = useProfessionalMode()
  const routeApplicationId: unknown = useParams().applicationId
  const applicationId =
    typeof routeApplicationId === 'number' &&
    Number.isSafeInteger(routeApplicationId)
      ? routeApplicationId
      : null
  useEffect(() => {
    document.body.classList.add('app2-document')
    if (typeof window.Intercom !== 'function')
      return () => document.body.classList.remove('app2-document')
    window.Intercom('hide')
    window.Intercom('update', { hide_default_launcher: true })
    return () => {
      document.body.classList.remove('app2-document')
      window.Intercom('update', { hide_default_launcher: false })
    }
  }, [])
  return (
    <UiProvider theme="professional" mode={appearance.mode} className="app2-ui">
      <App2FrameTintProvider>
        <App2CasesProvider applicationId={applicationId}>
          <App2Frame applicationId={applicationId} {...appearance}>
            {children}
          </App2Frame>
        </App2CasesProvider>
      </App2FrameTintProvider>
    </UiProvider>
  )
}
export default App2DashboardLayout
